/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { bulkActionProgressAtom } from '../../states/bulk-action-progress';
import { BulkOperationState, BulkOperationStatus } from '../../typings/bulk-action-progress';

type PanelState = 'running' | 'success' | 'partial';

function getPanelState(status: BulkOperationStatus, failed: number): PanelState {
  if (status === 'QUEUED' || status === 'IN_PROGRESS') return 'running';
  if (status === 'COMPLETE' && failed === 0) return 'success';
  if (status === 'COMPLETE' && failed > 0) return 'partial';
  return 'running';
}

function getTitle(panelState: PanelState, action: string, total: number, succeeded: number): string {
  const n = total.toLocaleString();
  const s = succeeded.toLocaleString();
  if (panelState === 'running') {
    switch (action) {
      case 'assign':
        return `Assigning ${n} chats`;
      case 'unassign':
        return `Unassigning ${n} chats`;
      case 'add_tags':
        return `Adding tags to ${n} chats`;
      case 'add_members':
        return `Adding ${n} chats to broadcast list`;
      case 'mark_as_done':
        return `Marking ${n} chats as done`;
      default:
        return `Processing ${n} chats`;
    }
  }
  if (panelState === 'success') {
    switch (action) {
      case 'assign':
        return `${n} chats assigned`;
      case 'unassign':
        return `${n} chats unassigned`;
      case 'add_tags':
        return `Tags applied to ${n} chats`;
      case 'add_members':
        return `${n} chats added to broadcast list`;
      case 'mark_as_done':
        return `${n} chats marked as done`;
      default:
        return `${n} chats processed`;
    }
  }
  switch (action) {
    case 'assign':
      return `Assigned ${s} of ${n} chats`;
    case 'unassign':
      return `Unassigned ${s} of ${n} chats`;
    case 'add_tags':
      return `Tags applied to ${s} of ${n} chats`;
    case 'add_members':
      return `${s} of ${n} chats added to broadcast list`;
    case 'mark_as_done':
      return `Marked ${s} of ${n} chats as done`;
    default:
      return `${s} of ${n} chats processed`;
  }
}

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { opacity: 0; }
  50% { opacity: 1; }
  100% { opacity: 0; }
`;

const AUTO_DISMISS_MS = 10_000;

interface PanelContentProps {
  operation: BulkOperationState;
  onDismiss: () => void;
}

const PanelContent: FC<PanelContentProps> = memo(({ operation, onDismiss }) => {
  const { status, action, total, succeeded, failed } = operation;
  const panelState = getPanelState(status, failed);
  const title = getTitle(panelState, action, total, succeeded);
  const progressPct = total > 0 ? Math.round((succeeded / total) * 100) : 0;

  const isRunning = panelState === 'running';
  const isSuccess = panelState === 'success';
  const isPartial = panelState === 'partial';

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #e0e0ea',
        borderRadius: '4px',
        overflow: 'hidden',
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
        bgcolor: '#ffffff',
      }}
    >
      {!isSuccess && (
        <Box sx={{ height: '3px', bgcolor: '#f4f4f9', position: 'relative', overflow: 'hidden' }}>
          <Box
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              height: 100%;
              width: ${isPartial ? '100%' : `${progressPct}%`};
              background: ${isPartial ? '#ffa726' : '#3f906d'};
              transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
              border-radius: 0 1px 1px 0;
              ${isRunning
                ? `&::after {
                    content: '';
                    position: absolute;
                    right: 0;
                    top: 0;
                    width: 60px;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3));
                    animation: ${shimmer} 1.6s ease-in-out infinite;
                  }`
                : ''}
            `}
          />
        </Box>
      )}

      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 1.5, py: 1.25 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: isPartial ? 'rgba(255, 167, 38, 0.06)' : 'rgba(63, 144, 109, 0.08)',
          }}
        >
          {isRunning && (
            <svg
              css={css`
                width: 16px;
                height: 16px;
                color: #3f906d;
                animation: ${spin} 1s linear infinite;
              `}
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M8 2a6 6 0 1 1-5.2 3" strokeLinecap="round" />
            </svg>
          )}
          {isSuccess && (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="#29a471"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3.5 8.5l3 3 6-7" />
            </svg>
          )}
          {isPartial && (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="#ffa726"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <path d="M8 3L1.5 13.5h13L8 3z" />
              <path d="M8 7v3M8 11.5v.5" />
            </svg>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#2d2d2d', lineHeight: 1.4, mb: '1px' }} noWrap>
            {title}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            {isRunning && (
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#5f5f85', fontVariantNumeric: 'tabular-nums' }}>
                {succeeded.toLocaleString()} / {total.toLocaleString()}
              </Typography>
            )}
            {isSuccess && <Typography sx={{ fontSize: 12, color: '#7a7a7a' }}>Completed</Typography>}
            {isPartial && (
              <>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#e6901a' }}>
                  {failed.toLocaleString()} failed
                </Typography>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: '#e0e0ea', flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12, color: '#7a7a7a' }}>Could not complete</Typography>
              </>
            )}
          </Stack>
        </Box>

        <Button
          onClick={onDismiss}
          size="small"
          sx={{
            minWidth: 'unset',
            flexShrink: 0,
            fontSize: 12,
            fontWeight: 500,
            color: '#7a7a7a',
            textTransform: 'none',
            px: 1,
            py: 0.5,
            borderRadius: '4px',
            '&:hover': { bgcolor: '#f5f5f5', color: '#2d2d2d' },
          }}
        >
          {isRunning ? 'Hide' : 'Dismiss'}
        </Button>
      </Stack>

      {isPartial && (
        <Box sx={{ px: 1.5, pb: 1.25 }}>
          <Box
            sx={{
              bgcolor: 'rgba(255, 167, 38, 0.06)',
              border: '1px solid rgba(255, 167, 38, 0.2)',
              borderRadius: '4px',
              px: 1.25,
              py: 1,
            }}
          >
            <Typography sx={{ fontSize: 12, color: '#5f5f85', lineHeight: 1.4 }}>
              <Box component="strong" sx={{ fontWeight: 600, color: '#2d2d2d' }}>
                {failed.toLocaleString()} chats
              </Box>{' '}
              could not be processed.
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
});

PanelContent.displayName = 'PanelContent';

interface OperationPanelProps {
  operation: BulkOperationState;
  onDismiss: () => void;
}

const OperationPanel: FC<OperationPanelProps> = memo(({ operation, onDismiss }) => {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const handleDismiss = useCallback(() => {
    setVisible(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onDismissRef.current(), 350);
  }, []);

  useEffect(() => {
    setVisible(true);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    const isSuccess = operation.status === 'COMPLETE' && operation.failed === 0;
    const isPartial = operation.status === 'COMPLETE' && operation.failed > 0;
    if (!isSuccess && !isPartial) return;
    timerRef.current = setTimeout(handleDismiss, AUTO_DISMISS_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [operation.status, operation.failed, handleDismiss]);

  return (
    <Box
      sx={{
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        opacity: visible ? 1 : 0,
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease-in-out',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <PanelContent operation={operation} onDismiss={handleDismiss} />
    </Box>
  );
});

OperationPanel.displayName = 'OperationPanel';

const BulkActionProgressPanel: FC = memo(() => {
  const operations = useAtomValue(bulkActionProgressAtom);
  const setBulkActionProgress = useSetAtom(bulkActionProgressAtom);
  const portalContainer = document.getElementById('portal');

  const handleDismiss = useCallback(
    (operationId: string) => {
      setBulkActionProgress((prev) => prev.filter((op) => op.operationId !== operationId));
    },
    [setBulkActionProgress]
  );

  if (operations.length === 0 || !portalContainer) return null;

  return createPortal(
    <Stack
      spacing={1}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: 400,
        zIndex: 9999,
      }}
    >
      {[...operations].reverse().map((op) => (
        <OperationPanel key={op.operationId} operation={op} onDismiss={() => handleDismiss(op.operationId)} />
      ))}
    </Stack>,
    portalContainer
  );
});

BulkActionProgressPanel.displayName = 'BulkActionProgressPanel';

export default BulkActionProgressPanel;
